import { useSyncExternalStore } from 'react'
import { registerSW } from 'virtual:pwa-register'
import {
  CHECK_INTERVAL_MS,
  isServerAhead,
  shouldCheckNow,
  updateReadiness,
  type ApplyMode,
} from '@/lib/webUpdatePolicy'

/**
 * Keeps browser and Bhookly Server APK tabs on the current frontend build
 * (F-FE-N191). The Electron app has its own updater and never calls this.
 *
 * Two independent signals, because either alone has a blind spot:
 *   1. `registration.update()` on a timer + focus / visibility / online — the
 *      browser only re-checks sw.js on a real navigation, which a POS tab open
 *      for days never makes. A new worker installs and then WAITS (prompt
 *      mode; see vite.config.ts) — vite-plugin-pwa reports it via
 *      onNeedRefresh, and applying sends it SKIP_WAITING.
 *   2. `/version.json` vs the baked-in `__APP_BUILD_ID__` — notices a deploy
 *      even with no worker, and catches a worker that never moves (then the
 *      tab reloads around it after `SW_STUCK_MS`).
 *
 * Applying is the banner's job (`WebUpdateBanner`), at a safe moment.
 */

export interface WebUpdateSnapshot {
  /** How to apply, or null when this tab is current. */
  mode: ApplyMode | null
  /** The newer build's short commit, when the server reported one. */
  remoteCommit: string
}

let workerWaiting = false
let workerActivated = false
let activateWaiting: (() => Promise<void>) | undefined
let serverAheadSince: number | null = null
let remoteCommit = ''
let registration: ServiceWorkerRegistration | undefined
let lastCheckAt: number | null = null
let started = false

let snapshot: WebUpdateSnapshot = { mode: null, remoteCommit: '' }
const listeners = new Set<() => void>()

function controlled(): boolean {
  return typeof navigator !== 'undefined' && Boolean(navigator.serviceWorker?.controller)
}

function recompute(): void {
  const mode = updateReadiness({
    workerWaiting,
    workerActivated,
    serverAheadSince,
    controlled: controlled(),
    now: Date.now(),
  })
  if (mode === snapshot.mode && remoteCommit === snapshot.remoteCommit) return
  snapshot = { mode, remoteCommit }
  for (const l of listeners) l()
}

/**
 * Not routed through `api/client.ts` on purpose: this is a static file the
 * frontend container serves next to index.html, not a backend call, and it
 * must bypass every cache (nginx sends no-store; `cache: 'no-store'` covers
 * the HTTP cache; the service worker does not precache json).
 */
async function fetchServerBuild(): Promise<{ buildId?: unknown; commit?: unknown } | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
    if (!res.ok) return null
    return (await res.json()) as { buildId?: unknown; commit?: unknown }
  } catch {
    return null
  }
}

async function check(): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return
  const now = Date.now()
  if (!shouldCheckNow(lastCheckAt, now)) return
  lastCheckAt = now

  // Never let a failed check surface: a stale tab is the status quo, not an error.
  if (registration && !registration.installing) {
    await registration.update().catch(() => {})
  }

  const server = await fetchServerBuild()
  if (server && isServerAhead(__APP_BUILD_ID__, server.buildId)) {
    serverAheadSince ??= Date.now()
    remoteCommit = typeof server.commit === 'string' ? server.commit : ''
  } else if (server) {
    // Server matches us again (e.g. a rollback to this build): nothing to do.
    serverAheadSince = null
    remoteCommit = ''
  }
  recompute()
}

/** Register the service worker and start watching for new builds. Idempotent. */
export function startWebUpdates(): void {
  if (started || typeof window === 'undefined') return
  started = true

  activateWaiting = registerSW({
    immediate: true,
    onRegisteredSW(_url, reg) {
      registration = reg
    },
    // A new build is installed and waiting. We only record it — the banner
    // picks the moment. (Workbox also reports a worker that was already
    // waiting before this page registered, e.g. after a hard refresh.)
    onNeedRefresh() {
      workerWaiting = true
      recompute()
    },
    // The waiting worker took control. Without this hook vite-plugin-pwa
    // reloads EVERY open tab on the spot — including a second tab mid-checkout
    // when the first tab applied the update. The tab that applied reloads
    // itself in applyWebUpdate; any other tab now waits for its own safe moment.
    onNeedReload() {
      workerActivated = true
      recompute()
    },
  })

  // The dev server emits no version.json and runs no worker.
  if (!import.meta.env.PROD) return

  const poke = () => {
    void check()
  }
  window.setInterval(poke, CHECK_INTERVAL_MS)
  window.setTimeout(poke, 30_000)
  window.addEventListener('focus', poke)
  window.addEventListener('online', poke)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') poke()
  })
}

/** How long to wait for the new worker to take control before reloading anyway. */
const ACTIVATE_TIMEOUT_MS = 5_000

function waitForControllerChange(timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    const sw = navigator.serviceWorker
    if (!sw) return resolve()
    const done = () => {
      sw.removeEventListener('controllerchange', done)
      window.clearTimeout(timer)
      resolve()
    }
    const timer = window.setTimeout(done, timeoutMs)
    sw.addEventListener('controllerchange', done)
  })
}

/** Move this tab onto the new build. The page reloads. */
export async function applyWebUpdate(mode: ApplyMode): Promise<void> {
  if (mode === 'activate' && activateWaiting) {
    // SKIP_WAITING, then wait for the worker to take control so the reload is
    // served by it. A page the worker does not control never sees
    // controllerchange; the timeout covers a worker that is slow to take over.
    const changed = waitForControllerChange(ACTIVATE_TIMEOUT_MS)
    await activateWaiting()
    if (navigator.serviceWorker?.controller) await changed
  } else if (mode === 'bypass' && typeof navigator !== 'undefined' && navigator.serviceWorker) {
    const regs = await navigator.serviceWorker.getRegistrations().catch(() => [])
    await Promise.all(regs.map((r) => r.unregister().catch(() => false)))
  }
  window.location.reload()
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange)
  return () => {
    listeners.delete(onChange)
  }
}

function getSnapshot(): WebUpdateSnapshot {
  return snapshot
}

export function useWebUpdate(): WebUpdateSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
